/**
 * Where a page's Back control should return to when the screen was opened from
 * somewhere other than its own parent.
 *
 * A record's page sits at a fixed place in the tree — an asset inside a folder
 * inside a project — so its Back can only guess at that parent, and a board that
 * sent the person there from an entirely different section lands them somewhere
 * they have never been. The sender names the return leg in history state; the
 * page reads it and falls back to its parent when nobody did.
 *
 * History state is attacker-reachable in the sense that anything can push an
 * entry, so the value is accepted only as a same-origin absolute path: no
 * scheme, no host, and no protocol-relative `//host` that a bare leading-slash
 * check would wave through.
 */

/** The history-state shape a sender writes and a Back control reads. */
export interface BackToState {
  backTo?: string;
}

/** Build the state a link or `navigate` carries so the destination can come back here. */
export function backToState(path: string): BackToState {
  return { backTo: path };
}

/** The return leg held in history state, or null when there is none to trust. */
export function readBackTo(state: unknown): string | null {
  const backTo = (state as BackToState | null | undefined)?.backTo;
  if (typeof backTo !== 'string') return null;
  return backTo.startsWith('/') && !backTo.startsWith('//') ? backTo : null;
}

// `ROUTES.TOOLS_HUB`, restated because `shared/lib` may not import `shared/config`; the
// tests pin the two together.
const TOOLS_HUB = '/tools';

/** True when the origin is a Tools page (`/tools` or any `/tools/...` subpath). */
export const isToolsOrigin = (from: string | undefined): from is string =>
  !!from && (from === TOOLS_HUB || from.startsWith(`${TOOLS_HUB}/`));
